import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import {
  Loader2, Settings2, TrendingUp, TrendingDown, Minus, Bot, Package, Calendar,
  Link2, AlertTriangle, Activity,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const zar = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;

const marginBadge = (pct) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-zinc-400">—</span>;
  const cls = pct >= 60 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-red-50 text-red-700 border-red-200';
  return <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full border ${cls}`}>{pct}%</span>;
};

const utilBadge = (pct) => {
  if (pct === null || pct === undefined) return <span className="text-xs text-zinc-400">—</span>;
  const cls = pct > 90 ? 'bg-violet-50 text-violet-700 border-violet-200'
    : pct < 30 ? 'bg-red-50 text-red-600 border-red-200'
    : 'bg-zinc-50 text-zinc-600 border-zinc-200';
  const tag = pct > 90 ? 'upsell' : pct < 30 ? 'churn risk' : null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      {pct}%{tag && <span className="font-bold">· {tag}</span>}
    </span>
  );
};

const ASSUMPTION_FIELDS = [
  { key: 'recall_usd_per_hr', label: 'Recall.ai bot ($/hr)' },
  { key: 'transcription_usd_per_hr', label: 'Transcription ($/hr)' },
  { key: 'ai_usd_per_hr', label: 'AI analysis ($/hr)' },
  { key: 'research_usd_each', label: 'Prospect research ($/each)' },
  { key: 'hosting_zar_per_month', label: 'Hosting (R/month)' },
  { key: 'fx_zar_per_usd', label: 'Exchange rate (R per $)' },
];

const ADOPTION_META = {
  meeting_bots: { label: 'Meeting Bots', icon: Bot },
  product_intelligence: { label: 'Product Intelligence', icon: Package },
  calendar_sync: { label: 'Calendar Sync', icon: Calendar },
  crm_integration: { label: 'CRM Integration', icon: Link2 },
};

export const PlatformCosts = () => {
  const [overview, setOverview] = useState(null);
  const [insights, setInsights] = useState(null);
  const [assumptions, setAssumptions] = useState(null);
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [ov, ins, asm] = await Promise.all([
        axios.get(`${API}/platform/costs/overview`),
        axios.get(`${API}/platform/costs/insights`),
        axios.get(`${API}/platform/costs/assumptions`),
      ]);
      setOverview(ov.data);
      setInsights(ins.data);
      setAssumptions(asm.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load cost analytics');
    }
  };

  useEffect(() => { load(); }, []);

  const saveAssumptions = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(ASSUMPTION_FIELDS.map(f => [f.key, parseFloat(assumptions[f.key])]));
      await axios.put(`${API}/platform/costs/assumptions`, payload);
      toast.success('Cost assumptions saved — recalculating');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save assumptions');
    } finally { setSaving(false); }
  };

  if (error) return <div className="border border-red-200 bg-red-50 rounded-lg p-6 text-sm text-red-700" data-testid="costs-error">{error}</div>;
  if (!overview || !insights) return (
    <div className="flex items-center justify-center py-20" data-testid="costs-loading">
      <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
    </div>
  );

  const t = overview.totals;
  const bd = t.breakdown;
  const bdTotal = Object.values(bd).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6" data-testid="costs-tab">
      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-zinc-200"><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Est. COGS (this month)</p>
          <p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="total-cogs">{zar(t.est_cost_zar)}</p>
        </CardContent></Card>
        <Card className="border-zinc-200"><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Revenue (MRR + packs)</p>
          <p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="total-revenue">{zar(t.revenue_zar)}</p>
        </CardContent></Card>
        <Card className="border-zinc-200"><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Blended Gross Margin</p>
          <p className={`text-2xl font-bold mt-1 ${t.blended_margin_pct === null ? 'text-zinc-400' : t.blended_margin_pct >= 60 ? 'text-emerald-600' : t.blended_margin_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`} data-testid="blended-margin">
            {t.blended_margin_pct === null ? '—' : `${t.blended_margin_pct}%`}
          </p>
        </CardContent></Card>
        <Card className="border-zinc-200"><CardContent className="pt-5 pb-4">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Call Minutes Consumed</p>
          <p className="text-2xl font-bold text-zinc-900 mt-1" data-testid="total-minutes">{Math.round(t.total_minutes).toLocaleString()}</p>
        </CardContent></Card>
      </div>

      {/* Cost breakdown + assumptions */}
      <Card className="border-zinc-200"><CardContent className="pt-5 pb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-zinc-800">Cost breakdown by driver</p>
          <Button variant="outline" size="sm" className="rounded-md" onClick={() => setShowAssumptions(v => !v)} data-testid="toggle-assumptions-btn">
            <Settings2 className="w-4 h-4 mr-1.5" /> Cost assumptions
          </Button>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden mb-2">
          <div className="bg-[#0E4E5A]" style={{ width: `${bd.recall_zar / bdTotal * 100}%` }} />
          <div className="bg-[#7CD9CC]" style={{ width: `${bd.transcription_zar / bdTotal * 100}%` }} />
          <div className="bg-amber-400" style={{ width: `${bd.ai_zar / bdTotal * 100}%` }} />
          <div className="bg-violet-400" style={{ width: `${(bd.research_zar || 0) / bdTotal * 100}%` }} />
          <div className="bg-zinc-300" style={{ width: `${bd.hosting_zar / bdTotal * 100}%` }} />
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-600">
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#0E4E5A] mr-1.5" />Recall.ai bots {zar(bd.recall_zar)}</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#7CD9CC] mr-1.5" />Transcription {zar(bd.transcription_zar)}</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-400 mr-1.5" />AI analysis {zar(bd.ai_zar)}</span>
          <span data-testid="breakdown-research"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-violet-400 mr-1.5" />Prospect research {zar(bd.research_zar || 0)} ({t.total_researches || 0} runs)</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-zinc-300 mr-1.5" />Hosting (fixed) {zar(bd.hosting_zar)}</span>
        </div>

        {showAssumptions && assumptions && (
          <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="assumptions-panel">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              {ASSUMPTION_FIELDS.map(f => (
                <div key={f.key}>
                  <label className="text-xs text-zinc-500">{f.label}</label>
                  <Input
                    type="number" step="0.01" value={assumptions[f.key]}
                    onChange={e => setAssumptions(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="rounded-md h-9 mt-1" data-testid={`assumption-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <Button size="sm" className="mt-3 rounded-md bg-brand-primary hover:bg-brand-hover" onClick={saveAssumptions} disabled={saving} data-testid="save-assumptions-btn">
              {saving ? 'Saving…' : 'Save & recalculate'}
            </Button>
          </div>
        )}
      </CardContent></Card>

      {/* Per-org margin table */}
      <Card className="border-zinc-200"><CardContent className="pt-5 pb-5">
        <p className="text-sm font-semibold text-zinc-800 mb-3">Per-organization economics (this month)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="org-costs-table">
            <thead>
              <tr className="text-left text-xs text-zinc-500 uppercase tracking-wide border-b border-zinc-100">
                <th className="py-2 pr-3">Organization</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3 text-right">Minutes</th>
                <th className="py-2 pr-3 text-right">Tokens</th>
                <th className="py-2 pr-3 text-right">Researches</th>
                <th className="py-2 pr-3">Utilization</th>
                <th className="py-2 pr-3 text-right">Est. Cost</th>
                <th className="py-2 pr-3 text-right">Revenue</th>
                <th className="py-2">Margin</th>
              </tr>
            </thead>
            <tbody>
              {overview.orgs.map(o => (
                <tr key={o.org_id} className="border-b border-zinc-50 hover:bg-zinc-50/60" data-testid={`org-cost-row-${o.org_id}`}>
                  <td className="py-2.5 pr-3 font-medium text-zinc-800">{o.org_name}</td>
                  <td className="py-2.5 pr-3 capitalize text-zinc-600">{o.plan}</td>
                  <td className="py-2.5 pr-3 text-right text-zinc-600">{Math.round(o.minutes)}</td>
                  <td className="py-2.5 pr-3 text-right text-zinc-600">{o.tokens_consumed}{o.tokens_included ? ` / ${o.tokens_included}` : ''}</td>
                  <td className="py-2.5 pr-3 text-right text-zinc-600" data-testid={`org-researches-${o.org_id}`}>{o.researches || 0}{o.research_cost_zar ? <span className="text-xs text-zinc-400"> ({zar(o.research_cost_zar)})</span> : ''}</td>
                  <td className="py-2.5 pr-3">{utilBadge(o.utilization_pct)}</td>
                  <td className="py-2.5 pr-3 text-right text-zinc-700">{zar(o.est_cost_zar)}</td>
                  <td className="py-2.5 pr-3 text-right text-zinc-700">{zar(o.revenue_zar)}</td>
                  <td className="py-2.5">{marginBadge(o.margin_pct)}</td>
                </tr>
              ))}
              {overview.orgs.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-zinc-400 text-sm">No usage or revenue recorded this month yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Product insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-zinc-200"><CardContent className="pt-5 pb-5" data-testid="adoption-panel">
          <p className="text-sm font-semibold text-zinc-800 mb-3">Feature adoption <span className="text-zinc-400 font-normal">({insights.total_active_orgs} active orgs)</span></p>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(insights.adoption).map(([k, v]) => {
              const meta = ADOPTION_META[k] || { label: k, icon: Activity };
              const Icon = meta.icon;
              return (
                <div key={k} className="border border-zinc-100 rounded-xl p-3 bg-zinc-50/50">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1"><Icon className="w-3.5 h-3.5 text-brand-primary" />{meta.label}</div>
                  <p className="text-lg font-bold text-zinc-900">{v.pct}% <span className="text-xs font-normal text-zinc-400">({v.orgs} orgs)</span></p>
                </div>
              );
            })}
          </div>
          <div className="mt-4 border-t border-zinc-100 pt-3 flex items-center justify-between text-sm">
            <span className="text-zinc-500">Avg calls per rep / week</span>
            <span className="font-bold text-zinc-900" data-testid="calls-per-rep-week">{insights.engagement.avg_calls_per_rep_week}</span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-zinc-500">Calls this month / active reps</span>
            <span className="font-medium text-zinc-700">{insights.engagement.calls_this_month} / {insights.engagement.active_reps}</span>
          </div>
        </CardContent></Card>

        <Card className="border-zinc-200"><CardContent className="pt-5 pb-5" data-testid="quiet-orgs-panel">
          <p className="text-sm font-semibold text-zinc-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" /> Quiet orgs — churn risk (14+ days, no calls)
          </p>
          {insights.engagement.quiet_orgs.length === 0 ? (
            <p className="text-sm text-zinc-400">None — every org has called recently 🎉</p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {insights.engagement.quiet_orgs.map(q => (
                <div key={q.org_id} className="flex items-center justify-between text-sm border border-zinc-100 rounded-lg px-3 py-2">
                  <span className="font-medium text-zinc-700 truncate">{q.org_name}</span>
                  <span className="text-xs text-zinc-500 shrink-0 ml-2">
                    {q.ever_called ? `${q.days_quiet ?? '?'}d since last call` : 'never called'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      </div>

      {/* Quality trends */}
      <Card className="border-zinc-200"><CardContent className="pt-5 pb-5" data-testid="quality-panel">
        <p className="text-sm font-semibold text-zinc-800 mb-3">Call quality — avg qualification score by org</p>
        {insights.quality.length === 0 ? (
          <p className="text-sm text-zinc-400">No scored calls in the last two months</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {insights.quality.map(q => (
              <div key={q.org_id} className="flex items-center justify-between border border-zinc-100 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-zinc-700 truncate">{q.org_name}</span>
                <span className="flex items-center gap-1.5 shrink-0 ml-2">
                  {q.avg_prev_month !== null && q.avg_prev_month !== undefined && <span className="text-xs text-zinc-400">{q.avg_prev_month}%</span>}
                  {q.delta === null || q.delta === undefined ? <Minus className="w-3.5 h-3.5 text-zinc-300" />
                    : q.delta > 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                    : q.delta < 0 ? <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                    : <Minus className="w-3.5 h-3.5 text-zinc-300" />}
                  <span className="font-bold text-zinc-900">{q.avg_this_month ?? '—'}{q.avg_this_month !== null && q.avg_this_month !== undefined ? '%' : ''}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};
